import { useInfiniteQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { LightUserSchema } from 'shared/hooks/use-load-light-users';
import {
  type ApiError,
  addResourceNameToInfiniteQueryResult,
  handleZodError,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { PaginationSchema } from 'types/pagination';
import z from 'zod/v4';

import { AutomationActionTypeSchema, AutomationModelSchema } from '../pages';

export const AutomationLogSchema = z.discriminatedUnion('actionType', [
  z.object({
    actionType: z.literal('AssignAccountManager'),
    date: z.coerce.date(),
    field: z.string(),
    identifier: z.string(),
    model: AutomationModelSchema,
    newValue: LightUserSchema,
    resourceId: z.number(),
  }),
  z.object({
    actionType: AutomationActionTypeSchema.exclude(['AssignAccountManager']),
    date: z.coerce.date(),
    field: z.string(),
    identifier: z.string(),
    model: AutomationModelSchema,
    newValue: z.string().nullable(),
    resourceId: z.number(),
  }),
]);

const LoadAutomationLogsSchema = z.object({
  data: z.array(AutomationLogSchema),
  pagination: PaginationSchema,
});

export type AutomationLogs = z.infer<typeof LoadAutomationLogsSchema>;
export type AutomationLog = z.infer<typeof AutomationLogSchema>;

export async function loadAutomationLogsInfiniteQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey, pageParam }: any,
) {
  const instance = await axiosInstance;
  const { automationId, ...filterParams } = queryKey[1];
  const { data } = await instance.get(`/automations/${automationId}/logs`, {
    params: { page: pageParam, ...filterParams },
  });

  const safeParse = LoadAutomationLogsSchema.safeParse(data);

  if (!safeParse.success) {
    handleZodError(safeParse, 'automationLogs');
    throw safeParse.error;
  }

  return safeParse.data;
}

type LoadAutomationInfiniteVariables = {
  automationId: string | null;
  enabled?: boolean;
  pageLimit: number;
};

const emptyAutomationLogs: { pages: AutomationLogs[]; pageParams: unknown[] } = {
  pages: [{ data: [], pagination: { totalObjects: 0, totalPages: 0, pageLimit: 0 } }],
  pageParams: [],
};

export function useLoadAutomationLogsInfinite({
  automationId,
  enabled = true,
  ...filterParams
}: LoadAutomationInfiniteVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useInfiniteQuery<AutomationLogs, ApiError>({
    queryKey: ['automation-logs-infinite', { automationId, ...filterParams }],
    queryFn: context => loadAutomationLogsInfiniteQueryFn(axiosInstance, context),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const { pagination } = lastPage;
      const currentPageIndex = allPages.length;
      const hasNextPage = currentPageIndex < pagination.totalPages;
      return hasNextPage ? currentPageIndex + 1 : undefined;
    },
    enabled,
    staleTime: 1,
    placeholderData: previousData => previousData ?? emptyAutomationLogs,
  });

  return addResourceNameToInfiniteQueryResult<
    AutomationLogs,
    ApiError,
    'automationLogs',
    'PreventUndefined'
  >('automationLogs', queryResult, emptyAutomationLogs);
}
